import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { raffleApi, purchaseApi } from '../lib/api';
import { ArrowLeft, Ticket, Clock, MessageCircle, X, Check } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

// Helper function to format ticket numbers with leading zeros
const formatTicketNumber = (num, totalTickets) => {
  const digits = String(totalTickets).length;
  return String(num).padStart(digits, '0');
};

export default function RaffleDetailPage() {
  const { id } = useParams();
  const [raffle, setRaffle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [purchaseResult, setPurchaseResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    buyer_name: '',
    buyer_phone: '',
    buyer_email: ''
  });

  useEffect(() => {
    const fetchRaffle = async () => {
      try {
        const response = await raffleApi.getById(id);
        setRaffle(response.data);
      } catch (error) {
        console.error('Error fetching raffle:', error);
        toast.error('Error al cargar el sorteo');
      } finally {
        setLoading(false);
      }
    };
    fetchRaffle();
  }, [id]);

  const toggleTicket = (ticketNumber) => {
    if (raffle.reserved_tickets.includes(ticketNumber)) return;
    
    setSelectedTickets(prev => 
      prev.includes(ticketNumber)
        ? prev.filter(t => t !== ticketNumber)
        : [...prev, ticketNumber]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedTickets.length === 0) {
      toast.error('Selecciona al menos un boleto');
      return;
    }

    setSubmitting(true);
    try {
      const response = await purchaseApi.create({
        raffle_id: id,
        tickets: selectedTickets,
        ...form
      });
      setPurchaseResult(response.data);
      setShowCheckout(false);
      setShowSuccess(true);
      // Refresh raffle to update reserved tickets
      const updatedRaffle = await raffleApi.getById(id);
      setRaffle(updatedRaffle.data);
      setSelectedTickets([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar la compra');
    } finally {
      setSubmitting(false);
    }
  };

  const generateWhatsAppLink = () => {
    if (!purchaseResult || !raffle) return '#';
    const endTicket = (raffle.start_ticket || 1) + raffle.total_tickets - 1;
    const formattedTickets = purchaseResult.tickets.map(t => formatTicketNumber(t, endTicket)).join(', ');
    const message = `Hola! Acabo de apartar los boletos ${formattedTickets} del sorteo "${purchaseResult.raffle_title}". Total: $${purchaseResult.total_amount} MXN. Mi nombre es ${form.buyer_name}.`;
    return `https://wa.me/${purchaseResult.whatsapp_number}?text=${encodeURIComponent(message)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-black border-t-transparent"></div>
      </div>
    );
  }

  if (!raffle) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <h1 className="text-2xl font-bold">Sorteo no encontrado</h1>
        <Link to="/" className="text-[#FF3333] font-bold">Volver al inicio</Link>
      </div>
    );
  }

  const daysLeft = Math.max(0, Math.ceil((new Date(raffle.end_date) - new Date()) / (1000 * 60 * 60 * 24)));

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b-2 border-black">
        <div className="px-4 sm:px-8 max-w-7xl mx-auto">
          <div className="flex items-center justify-between h-16">
           <Link to="/" className="font-black text-2xl tracking-tighter">
                <img className="w-36" src="/images/logo.png" alt="Logo" />
            </Link>
            <Link 
              to="/"
              className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider hover:text-[#FF3333] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Volver
            </Link>
          </div>
        </div>
      </header>

      <main className="px-4 sm:px-8 max-w-7xl mx-auto py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Column - Raffle Info */}
          <div className="lg:col-span-5 space-y-6">
            <div className="border-2 border-black shadow-[8px_8px_0_0_#000000]" data-testid="raffle-image">
              <img 
                src={raffle.image_url}
                alt={raffle.title}
                className="w-full h-[300px] lg:h-[400px] object-cover"
              />
            </div>

            <div className="space-y-4">
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">{raffle.title}</h1>
              <p className="text-[#4B5563] leading-relaxed">{raffle.description}</p>
              
              <div className="border-2 border-black p-4 space-y-2">
                <h3 className="font-bold">Premio:</h3>
                <p className="text-[#4B5563]">{raffle.prize_description}</p>
              </div>

              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <Ticket className="w-5 h-5 text-[#FF3333]" />
                  <span className="font-bold">${raffle.ticket_price} MXN</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#4B5563]" />
                  <span>{daysLeft} días restantes</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Ticket Selector */}
          <div className="lg:col-span-7 space-y-6">
            <div className="bg-[#F3F4F6] border-2 border-black p-6">
              <div className="flex flex-wrap items-center justify-between mb-6">
                <h2 className="text-2xl sm:mb-4 font-bold">Selecciona tus boletos</h2>
                <div className="flex items-center gap-4 text-xs">
                  <span className="flex items-center gap-1">
                    <div className="w-4 h-4 bg-white border-2 border-black"></div> Disponible
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-4 h-4 bg-[#FF3333] border-2 border-black"></div> Seleccionado
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-4 h-4 bg-[#D1D5DB] border-2 border-[#9CA3AF]"></div> Apartado
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-10 gap-2 max-h-[400px] overflow-y-auto p-2">
                {Array.from({ length: raffle.total_tickets }, (_, i) => i + (raffle.start_ticket || 1)).map((num) => {
                  const isReserved = raffle.reserved_tickets.includes(num);
                  const isSelected = selectedTickets.includes(num);
                  const endTicket = (raffle.start_ticket || 1) + raffle.total_tickets - 1;
                  
                  return (
                    <button
                      key={num}
                      onClick={() => toggleTicket(num)}
                      disabled={isReserved}
                      className={`
                        h-10 w-full sm:h-12 flex items-center justify-center font-bold text-xs sm:text-sm border-2 transition-all
                        ${isReserved 
                          ? 'bg-[#D1D5DB] text-[#9CA3AF] hidden border-[#9CA3AF] cursor-not-allowed' 
                          : isSelected
                            ? 'bg-[#FF3333] text-white border-black scale-95'
                            : 'bg-white text-black border-black hover:bg-gray-100 cursor-pointer'
                        }
                      `}
                      data-testid="ticket-button"
                    >
                      {formatTicketNumber(num, endTicket)}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Cart Summary */}
            <div 
              className="sticky bottom-4 bg-[#0A0A0A] text-white border-2 border-black p-6 shadow-[8px_8px_0_0_#FF3333]"
              data-testid="cart-summary"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-gray-400">Boletos seleccionados</p>
                  <p className="font-bold text-lg">
                    {selectedTickets.length > 0 
                      ? selectedTickets.sort((a, b) => a - b).map(t => formatTicketNumber(t, (raffle.start_ticket || 1) + raffle.total_tickets - 1)).join(', ')
                      : 'Ninguno'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-400">Total</p>
                  <p className="font-bold text-2xl text-[#FF3333]">
                    ${(selectedTickets.length * raffle.ticket_price).toFixed(2)} MXN
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowCheckout(true)}
                disabled={selectedTickets.length === 0}
                className={`
                  w-full font-bold uppercase tracking-wider py-3 border-2 transition-all
                  ${selectedTickets.length === 0
                    ? 'bg-gray-600 text-gray-400 border-gray-600 cursor-not-allowed'
                    : 'bg-[#FF3333] text-white border-white hover:bg-[#e62e2e]'
                  }
                `}
                data-testid="checkout-button"
              >
                Apartar Boletos ({selectedTickets.length})
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Checkout Modal */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="sm:max-w-md border-2 border-black rounded-none shadow-[8px_8px_0_0_#000000]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Completa tu compra</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="checkout-form">
            <div className="space-y-2">
              <Label htmlFor="name" className="font-bold">Nombre completo</Label>
              <Input
                id="name"
                value={form.buyer_name}
                onChange={(e) => setForm({ ...form, buyer_name: e.target.value })}
                required
                className="border-2 border-black rounded-none focus:ring-0 focus:border-[#FF3333]"
                placeholder="Tu nombre"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone" className="font-bold">Teléfono (WhatsApp)</Label>
              <Input
                id="phone"
                type="tel"
                value={form.buyer_phone}
                onChange={(e) => setForm({ ...form, buyer_phone: e.target.value })}
                required
                className="border-2 border-black rounded-none focus:ring-0 focus:border-[#FF3333]"
                placeholder="52 123 456 7890"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="font-bold">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.buyer_email}
                onChange={(e) => setForm({ ...form, buyer_email: e.target.value })}
                required
                className="border-2 border-black rounded-none focus:ring-0 focus:border-[#FF3333]"
                placeholder="tu@email.com"
              />
            </div>

            <div className="bg-[#F3F4F6] border-2 border-black p-4 space-y-2">
              <p className="text-sm font-bold">Resumen:</p>
              <p className="text-sm">Boletos: {selectedTickets.sort((a, b) => a - b).map(t => formatTicketNumber(t, (raffle.start_ticket || 1) + raffle.total_tickets - 1)).join(', ')}</p>
              <p className="text-lg font-bold">Total: ${(selectedTickets.length * raffle.ticket_price).toFixed(2)} MXN</p>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#FF3333] text-white font-bold uppercase tracking-wider border-2 border-black px-6 py-3 shadow-[4px_4px_0_0_#000000] hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[2px_2px_0_0_#000000] transition-all disabled:opacity-50"
            >
              {submitting ? 'Procesando...' : 'Confirmar Apartado'}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Success Modal */}
      <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
        <DialogContent className="sm:max-w-md border-2 border-black rounded-none shadow-[8px_8px_0_0_#10B981]" data-testid="whatsapp-instructions">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-[#10B981] rounded-full flex items-center justify-center mx-auto">
              <Check className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold">¡Boletos Apartados!</h2>
            <p className="text-[#4B5563]">
              Tus boletos han sido apartados exitosamente. Para confirmar tu compra, 
              realiza el pago y envía tu comprobante por WhatsApp.
            </p>

            {purchaseResult && (
              <div className="bg-[#F3F4F6] border-2 border-black p-4 text-left space-y-2">
                <p className="font-bold">Datos bancarios:</p>
                <p className="text-sm whitespace-pre-line">{purchaseResult.bank_details}</p>
                <hr className="border-black" />
                <p className="text-sm"><strong>Boletos:</strong> {purchaseResult.tickets.map(t => formatTicketNumber(t, (raffle?.start_ticket || 1) + (raffle?.total_tickets || 100) - 1)).join(', ')}</p>
                <p className="text-lg font-bold">Total: ${purchaseResult.total_amount} MXN</p>
              </div>
            )}

            <a
              href={generateWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full bg-[#25D366] text-white font-bold uppercase tracking-wider border-2 border-black px-6 py-3 shadow-[4px_4px_0_0_#000000] hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[2px_2px_0_0_#000000] transition-all"
              data-testid="send-whatsapp-btn"
            >
              <MessageCircle className="w-5 h-5" />
              Enviar comprobante por WhatsApp
            </a>

            <button
              onClick={() => setShowSuccess(false)}
              className="text-sm text-[#4B5563] hover:text-black"
            >
              Cerrar
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
